import { defineStore } from "pinia"
import { computed, inject, ref } from "vue"
import { useFetchWrapper } from "@core/composables"
import type { Services } from "@services"
import { createUniqueId } from "@core/helpers"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { mapOptimAnalysesToTemplateView } from "@modules/courtage/composables/useOptimAnalyseTemplateMapper"
import type {
    OptimTurpeAnalyse,
    OptimTurpeAnalyseResponse,
    OptimTurpeEligibleMeter,
} from "@modules/courtage/types/optim-turpe"
import { isOptimTurpeEligibleSegment } from "@modules/courtage/constants/optim-turpe"
import { getSourceSegmentForOptim } from "@modules/courtage/utils/resolveSourceSegmentForOptim"

export const useOptimTurpeStore = defineStore("optimTurpe", () => {
    const services = inject<Services>("services")
    const buildingStore = useBuildingStore()

    const analyseResponse = ref<OptimTurpeAnalyseResponse | null>(null)
    const currentSourceId = ref<string>("")
    const currentAnalyseIndex = ref<number>(0)
    const analyseErrorMessage = ref<string | null>(null)

    const getEligibleMeters = computed<OptimTurpeEligibleMeter[]>(
        () =>
            buildingStore.getMeters.filter((meter) =>
                isOptimTurpeEligibleSegment(getSourceSegmentForOptim(meter))
            ) as OptimTurpeEligibleMeter[]
    )

    const getCurrentSource = computed(() =>
        getEligibleMeters.value.find(
            (meter) => meter._id_source === currentSourceId.value
        )
    )

    const getAnalyses = computed(() =>
        mapOptimAnalysesToTemplateView(
            currentSourceId.value,
            analyseResponse.value,
            getCurrentSource.value
        )
    )

    const getCurrentAnalyse = computed(
        () => getAnalyses.value[currentAnalyseIndex.value]
    )

    const getLastFailureMessage = computed(() => {
        if (
            !currentSourceId.value ||
            !analyseResponse.value?.sources?.[currentSourceId.value]
        ) {
            return null
        }
        const failures = analyseResponse.value.sources[currentSourceId.value]
            .filter((analyse) => analyse.status === "FAILURE")
            .sort((a, b) => {
                const left = new Date(a.updatedAt ?? 0).getTime()
                const right = new Date(b.updatedAt ?? 0).getTime()
                return right - left
            })
        return failures[0]?.data?.comment?.value
            ? String(failures[0].data?.comment?.value)
            : null
    })

    const {
        fetch: fetchAnalyseBySourceRaw,
        isFetching: isFetchingAnalyse,
        hasError: hasErrorAnalyse,
        error: errorAnalyse,
    } = useFetchWrapper(
        async (sourceId: string, controller?: AbortController) => {
            if (!sourceId || !services?.$getOptimAnalyseBySource) {
                return
            }
            currentSourceId.value = sourceId
            const response = await services.$getOptimAnalyseBySource(
                sourceId,
                controller
            )
            /**
             * Anti-race : si l'utilisateur a changé de PDL pendant le fetch
             * (clic rapide), on ignore la réponse stale pour ne pas écraser
             * `analyseResponse` avec les données du PDL précédent.
             */
            if (currentSourceId.value !== sourceId) {
                return
            }
            analyseResponse.value = response
            currentAnalyseIndex.value = 0
            analyseErrorMessage.value = null
        }
    )

    async function fetchAnalyseBySource(sourceId: string): Promise<void> {
        await fetchAnalyseBySourceRaw(sourceId)
    }

    async function createOptimAnalyse(params: {
        sourceId: string
        buildingId: string
        dateFrom: string
        dateTo: string
    }): Promise<{ task_id: string; event_name: string }> {
        if (!services?.$createOptimAnalyse) {
            throw new Error("Service $createOptimAnalyse non disponible")
        }
        /** Aligné homeys-front : même valeur que l’id de tâche / `task.request.event_name`. */
        const eventName = createUniqueId()
        const result = await services.$createOptimAnalyse({
            ...params,
            eventName,
        })
        return {
            task_id: result.task_id,
            event_name: result.event_name || eventName,
        }
    }

    function setCurrentSourceId(sourceId: string): void {
        currentSourceId.value = sourceId
        currentAnalyseIndex.value = 0
    }

    function setCurrentAnalyseIndex(index: number): void {
        currentAnalyseIndex.value = index
    }

    function setAnalyseErrorMessage(message: string | null): void {
        analyseErrorMessage.value = message
    }

    /**
     * Met à jour `url_rapport` / `date_expiration_rapport` d’une analyse en mémoire
     * (résultat tâche génération PDF), sans refetch GET optimturpe/source.
     */
    /**
     * Supprime une analyse en local puis appelle l’API ; en cas d’échec, restaure la liste et l’index courant.
     */
    async function deleteAnalyseOptimistic(analyseId: string): Promise<void> {
        if (!services?.$deleteOptimAnalyse) {
            throw new Error("Service $deleteOptimAnalyse non disponible")
        }
        const sourceId = currentSourceId.value
        const response = analyseResponse.value
        if (!sourceId || !response?.sources?.[sourceId]?.length) {
            return
        }
        const list = response.sources[sourceId]
        const index = list.findIndex((item) => item._id === analyseId)
        if (index === -1) {
            return
        }

        const previousCurrentIndex = currentAnalyseIndex.value
        const newList = [...list.slice(0, index), ...list.slice(index + 1)]
        const newLength = newList.length

        let nextIndex = previousCurrentIndex
        if (index < previousCurrentIndex) {
            nextIndex = previousCurrentIndex - 1
        } else if (index === previousCurrentIndex) {
            nextIndex =
                newLength > 0
                    ? Math.min(previousCurrentIndex, newLength - 1)
                    : 0
        }

        analyseResponse.value = {
            ...response,
            sources: {
                ...response.sources,
                [sourceId]: newList,
            },
        }
        currentAnalyseIndex.value = nextIndex

        try {
            await services.$deleteOptimAnalyse(analyseId)
        } catch (error) {
            const restoredList: OptimTurpeAnalyse[] = [...list]
            analyseResponse.value = {
                ...response,
                sources: {
                    ...response.sources,
                    [sourceId]: restoredList,
                },
            }
            currentAnalyseIndex.value = previousCurrentIndex
            throw error
        }
    }

    function patchAnalyseRapport(params: {
        analyseId: string
        url_rapport: string
        date_expiration_rapport?: string
    }): void {
        const sourceId = currentSourceId.value
        const response = analyseResponse.value
        if (!sourceId || !response?.sources?.[sourceId]?.length) {
            return
        }
        const list = response.sources[sourceId]
        const index = list.findIndex((item) => item._id === params.analyseId)
        if (index === -1) {
            return
        }
        const previous = list[index]
        const nextAnalyse = {
            ...previous,
            url_rapport: params.url_rapport,
            ...(params.date_expiration_rapport != null &&
            params.date_expiration_rapport !== ""
                ? {
                      date_expiration_rapport: params.date_expiration_rapport,
                  }
                : {}),
        }
        const nextList = [
            ...list.slice(0, index),
            nextAnalyse,
            ...list.slice(index + 1),
        ]
        analyseResponse.value = {
            ...response,
            sources: {
                ...response.sources,
                [sourceId]: nextList,
            },
        }
    }

    function $reset() {
        analyseResponse.value = null
        currentSourceId.value = ""
        currentAnalyseIndex.value = 0
        analyseErrorMessage.value = null
    }

    return {
        analyseResponse,
        currentSourceId,
        currentAnalyseIndex,
        analyseErrorMessage,
        getEligibleMeters,
        getCurrentSource,
        getAnalyses,
        getCurrentAnalyse,
        getLastFailureMessage,
        fetchAnalyseBySource,
        createOptimAnalyse,
        setCurrentSourceId,
        setCurrentAnalyseIndex,
        setAnalyseErrorMessage,
        patchAnalyseRapport,
        deleteAnalyseOptimistic,
        $reset,
        isFetchingAnalyse,
        hasErrorAnalyse,
        errorAnalyse,
    }
})
